import dayjs from 'dayjs';
import {
    PartnerPenaltyFiltersTypeInput,
    PartnerPenaltyStatus,
    PenaltySource,
    WarehousePenaltyFiltersTypeInput,
} from '../graphql/types';

/** UI 层筛选条件（Tab 对应的 status 不在此，由 Page 层单独拼） */
export interface PenaltyCenterFilters {
    /** 运单号原始串，支持分号/逗号/换行分隔多个 */
    trackingNumbers?: string;
    penaltySources?: PenaltySource[];
    /** 派送司机姓名，精确匹配（无司机下拉数据源，故为自由输入） */
    driverName?: string;
    /** 仅 warehouse 模式可选；DSP 模式下恒为 undefined（DSP 只能看自己的罚单） */
    dspId?: number;
}

export const getDefaultPenaltyCenterFilters = (): PenaltyCenterFilters => ({});

/**
 * UI filters → GraphQL 入参（不含 statuses）。
 * 空串/空数组一律转 null，避免后端把「空字符串」当成有效筛选条件。
 * 刻意不产出 dspId —— 数据隔离由后端从登录上下文取。
 */
export const buildPenaltyFiltersInput = (
    filters: PenaltyCenterFilters
): Omit<PartnerPenaltyFiltersTypeInput, 'statuses'> => ({
    trackingNumbers: filters.trackingNumbers?.trim() || null,
    penaltySources: filters.penaltySources?.length ? filters.penaltySources : null,
    driverName: filters.driverName?.trim() || null,
});

/**
 * UI filters → 仓库侧 GraphQL 入参。
 * 在共享字段之上补 warehouseId（后端据此做仓库权限校验）与可选的 dspId。
 */
export const buildWarehousePenaltyFiltersInput = (
    filters: PenaltyCenterFilters,
    warehouseId: number
): Omit<WarehousePenaltyFiltersTypeInput, 'statuses'> => ({
    ...buildPenaltyFiltersInput(filters),
    warehouseId,
    dspId: filters.dspId ?? null,
});

/**
 * 是否允许发起申诉。
 * 仅「待提交」且未过申诉截止时间；截止时间为空视为不限期。
 * 前端判定只用于按钮置灰，权威判定在后端（前端时钟不可信）。
 */
export const canAppeal = (
    status: PartnerPenaltyStatus,
    appealDeadlineAt: string | null | undefined,
    nowMillis: number
): boolean => {
    if (status !== PartnerPenaltyStatus.PENDING_SUBMIT) return false;
    if (appealDeadlineAt == null) return true;
    return dayjs(appealDeadlineAt).valueOf() > nowMillis;
};

/** 表格空值统一兜底，避免 null/空串渲染成空白格 */
export const displayOrDash = (value: string | number | null | undefined): string => {
    if (value == null) return '--';
    const text = String(value).trim();
    return text === '' ? '--' : text;
};

/**
 * 罚单管理中心本页的 ISO-8601 时间格式化：后端（PartnerPenaltyItemType）透传 `Instant.toString()`，
 * dayjs 可直接解析，不做 Number() 转换。不复用全局 formatEpochToDateTime——那个按 epoch 时间戳设计，
 * 传 ISO 字符串会被 Number() 转成 NaN。
 */
export const formatIsoDateTime = (isoString?: string | null): string => {
    if (isoString == null) return '--';
    return dayjs(isoString).format('YYYY-MM-DD HH:mm');
};
